/**
 * Данные финансового блока.
 *
 * Только через `POST /api/reports/:code/run` и `GET /api/reports/:code`:
 * те же права, тот же скоуп и та же запись в журнал, что у обычных отчётов.
 *
 * Ответы кешируются на пять минут по точному телу запроса. Возврат в раздел,
 * который уже открывали, показывает цифры сразу, а смена периода держит
 * прежние цифры на экране, пока не придут новые: графики перетекают
 * от старых значений к новым, а не мигают скелетоном.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError, cacheScope, onSessionChange } from '../api'
import type { CurrentUser, ReportDescriptor, ReportResult } from '../types'
import { toNumber } from '../format'

export type RunBody = Parameters<typeof api.runReport>[1]

/**
 * Запрос, который заведомо закончится отказом, и потому не отправляется:
 * человек ограничен по городам, а отчёт по городам не делится. Сервер
 * в этом случае отказывает целиком, и спрашивать его незачем.
 */
export interface Blocked {
  blocked: string
}

export type ReportRequest = RunBody | Blocked | null

const isBlocked = (body: ReportRequest): body is Blocked => body !== null && typeof body === 'object' && 'blocked' in body

const TTL = 5 * 60_000

const results = new Map<string, { at: number; value: ReportResult }>()
const inflight = new Map<string, Promise<ReportResult>>()

const keyOf = (code: string, body: RunBody) => `${cacheScope()}|${code}|${JSON.stringify(body)}`

function fetchReport(code: string, body: RunBody): Promise<ReportResult> {
  const key = keyOf(code, body)
  const pending = inflight.get(key)
  if (pending) return pending

  const promise = api
    .runReport(code, body)
    .then((value) => {
      // Ответ, пришедший после выхода, в кеш нового пользователя не попадает:
      // ключ строится от прав на момент запроса, а кеш к этому времени очищен.
      if (key.startsWith(`${cacheScope()}|`)) results.set(key, { at: Date.now(), value })
      return value
    })
    .finally(() => inflight.delete(key))

  inflight.set(key, promise)
  return promise
}

function cached(code: string, body: RunBody): ReportResult | null {
  const hit = results.get(keyOf(code, body))
  return hit && Date.now() - hit.at < TTL ? hit.value : null
}

// --- Отказ из-за среза прав -------------------------------------------------

/** Что закрыто — по-человечески, с согласованным глаголом. */
const SUBJECTS: Record<string, [string, string]> = {
  receivables: ['Долги', 'не делятся'],
  cashflow: ['Движение денег', 'не делится'],
  stock_current: ['Остатки на складах', 'не делятся'],
  stock_turnover: ['Оборачиваемость запасов', 'не делится'],
  margin_by_department: ['Маржинальность', 'не делится'],
  sales_by_group: ['Выручка по направлениям', 'не делится'],
  abc_customers: ['Продажи по клиентам', 'не делятся'],
  pnl_by_department: ['P&L', 'не делится'],
  payroll: ['ФОТ', 'не делится'],
  pnl_management: ['Управленческий P&L', 'не делится'],
  payroll_services: ['ФОТ', 'не делится'],
  gross_profit_manual: ['Ручная выгрузка валовой прибыли', 'не делится'],
}

let scopeNames: string[] = []

/** Названия городов, которыми ограничен человек: рамка блока сообщает их сюда. */
export function setScopeNames(names: string[]): void {
  scopeNames = names
}

// Всё, что помнит страница о прошлом пользователе, уходит вместе с ним.
onSessionChange(() => {
  results.clear()
  inflight.clear()
  descriptors.clear()
  scopeNames = []
})

const TAIL = 'Чтобы открыть — обратитесь к администратору.'

/**
 * «Долги не делятся по городам, а вам доступен только Екатеринбург».
 * Без названий (список городов ещё не пришёл) — без перечисления.
 */
export function scopeForbidden(code: string, names: string[] = scopeNames): string {
  const [subject, verb] = SUBJECTS[code] ?? ['Этот отчёт', 'не делится']
  if (names.length === 0) return `${subject} ${verb} по городам, а ваши права ограничены городами. ${TAIL}`
  const list = names.length === 1 ? `доступен только ${names[0]}` : `доступны только ${names.slice(0, -1).join(', ')} и ${names[names.length - 1]}`
  return `${subject} ${verb} по городам, а вам ${list}. ${TAIL}`
}

/**
 * Сервер отказывает словами про «измерение», понятными администратору.
 * Человеку — что именно закрыто, почему и к кому идти:
 * «Долги не делятся по городам, а вам доступен только Екатеринбург».
 */
export function humanForbidden(code: string, message: string): string {
  const dimension = /измерению «([^»]+)»/.exec(message)?.[1]
  if (!dimension || !/нельзя ограничить/.test(message)) return message
  if (dimension === 'Филиал') return scopeForbidden(code)
  const [subject, verb] = SUBJECTS[code] ?? ['Этот отчёт', 'не делится']
  return `${subject} ${verb} по признаку «${dimension}», а ваши права ограничены именно по нему. ${TAIL}`
}

/** Сообщение для упавшего запроса: что случилось, без повтора заголовка блока. */
function failureText(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.status >= 500) return `Сервер ответил ошибкой (${caught.status}). Цифры не показаны, чтобы не показать неверные.`
    return caught.message
  }
  return 'Сервер не отвечает — проверьте связь и повторите.'
}

export interface ReportState {
  /** loading — первых данных ещё нет; ready — есть (возможно, прежние). */
  status: 'idle' | 'loading' | 'ready' | 'forbidden' | 'error'
  data: ReportResult | null
  /** Пришёл новый запрос, а на экране пока прежние цифры. */
  refreshing: boolean
  message: string | null
  /** Повторить упавший запрос. */
  retry?: () => void
}

interface Stored extends ReportState {
  /** К какому запросу относится состояние: права, отчёт и тело. */
  key: string | null
}

const IDLE: ReportState = { status: 'idle', data: null, refreshing: false, message: null }
const LOADING: ReportState = { status: 'loading', data: null, refreshing: false, message: null }

/**
 * `body === null` — отчёт не запрашивается (нет права или не хватает
 * описания). Ошибка 403 отделена от поломки: раздел объясняет, что закрыт,
 * остальной экран работает.
 *
 * Состояние выводится из запроса синхронно, в том же рендере, где сменился
 * период: ответ из кеша показывается сразу, а если ответа нет — прежние
 * цифры помечаются как `refreshing`. Иначе был бы кадр, где подписи уже
 * новые, а цифры ещё старые и ничем не помечены.
 */
export function useReport(code: string, body: ReportRequest): ReportState {
  const blocked = isBlocked(body) ? body.blocked : null
  const serialized = body && !blocked ? JSON.stringify(body) : null
  const key = serialized ? `${cacheScope()}|${code}|${serialized}` : null
  const [attempt, setAttempt] = useState(0)
  const retry = useCallback(() => setAttempt((value) => value + 1), [])
  const [stored, setStored] = useState<Stored>(() => {
    const hit = serialized ? cached(code, JSON.parse(serialized) as RunBody) : null
    return hit ? { status: 'ready', data: hit, refreshing: false, message: null, key } : { ...(serialized ? LOADING : IDLE), key }
  })

  useEffect(() => {
    if (!serialized || !key) return

    const request = JSON.parse(serialized) as RunBody
    const hit = cached(code, request)
    if (hit) {
      setStored({ status: 'ready', data: hit, refreshing: false, message: null, key })
      return
    }

    let cancelled = false
    // Повтор после ошибки: вместо текста ошибки — снова загрузка.
    setStored((current) => (current.key === key && current.status === 'error' ? { ...LOADING, key } : current))
    fetchReport(code, request)
      .then((data) => {
        if (!cancelled) setStored({ status: 'ready', data, refreshing: false, message: null, key })
      })
      .catch((caught: unknown) => {
        if (cancelled) return
        const error = caught instanceof ApiError ? caught : null
        setStored({
          status: error?.isForbidden ? 'forbidden' : 'error',
          data: null,
          refreshing: false,
          message: error?.isForbidden ? humanForbidden(code, error.message) : failureText(caught),
          key,
        })
      })

    return () => {
      cancelled = true
    }
  }, [code, key, attempt])

  if (blocked) return { status: 'forbidden', data: null, refreshing: false, message: blocked }
  if (!key) return IDLE
  if (stored.key === key) return stored.status === 'error' ? { ...stored, retry } : stored
  const hit = cached(code, JSON.parse(serialized!) as RunBody)
  if (hit) return { status: 'ready', data: hit, refreshing: false, message: null }
  if (stored.status === 'ready' && stored.data) return { ...stored, refreshing: true }
  return LOADING
}

/**
 * Согласованный набор ответов для блока со сравнением.
 *
 * Текущий период может прийти из кеша мгновенно, а прошлый — через секунду.
 * Склеивать их нельзя: дельта посчиталась бы между новым июлем и старым
 * июнем, а колонки сравнения на миг показали бы одно и то же. Поэтому блок
 * получает либо все ответы для текущего выбора, либо целиком прежний набор —
 * вместе с прежними подписями (`view`), приглушённый как «обновляется».
 */
export function useSettled<S extends ReportState[], V>(states: [...S], view: V): { states: S; view: V; pending: boolean } {
  const snapshot = useRef<{ states: S; view: V } | null>(null)
  const settled = states.every((state) => !state.refreshing && state.status !== 'loading')
  if (settled) {
    snapshot.current = { states: states as S, view }
    return { states: states as S, view, pending: false }
  }
  const previous = snapshot.current
  if (previous && previous.states.every((state) => state.status === 'ready' || state.status === 'idle')) {
    return {
      states: previous.states.map((state) => (state.status === 'ready' ? { ...state, refreshing: true } : state)) as S,
      view: previous.view,
      pending: true,
    }
  }
  return { states: states as S, view, pending: true }
}

// --- Описания отчётов --------------------------------------------------------

export const FINANCE_REPORTS = ['margin_by_department', 'sales_by_group', 'abc_customers', 'cashflow', 'receivables', 'pnl_management', 'payroll_services'] as const
export type FinanceReport = (typeof FINANCE_REPORTS)[number]

const descriptors = new Map<string, Promise<ReportDescriptor | null>>()

function fetchDescriptor(code: string): Promise<ReportDescriptor | null> {
  // Дескриптор зависит от прав: скрытые показатели в нём не перечисляются.
  const key = `${cacheScope()}|${code}`
  let promise = descriptors.get(key)
  if (!promise) {
    promise = api.report(code).catch(() => {
      descriptors.delete(key)
      return null
    })
    descriptors.set(key, promise)
  }
  return promise
}

export interface Catalog {
  ready: boolean
  /** Отчёт выдан человеку и его описание получено. */
  has: (code: FinanceReport) => boolean
  descriptor: (code: FinanceReport) => ReportDescriptor | null
  /** Есть ли у отчёта измерение: филиал, сторона долга и т. п. */
  hasDimension: (code: FinanceReport, dimension: string) => boolean
}

export function useCatalog(user: CurrentUser): Catalog {
  // Отчёт, закрытый срезом прав (unavailable_reason), описания не отдаёт — 403; не спрашиваем.
  const allowed = FINANCE_REPORTS.filter((code) => user.reports.some((report) => report.code === code && !report.unavailable_reason))
  const allowedKey = allowed.join(',')
  const [loaded, setLoaded] = useState<Record<string, ReportDescriptor | null> | null>(null)

  useEffect(() => {
    let cancelled = false
    void Promise.all(allowedKey.split(',').filter(Boolean).map(async (code) => [code, await fetchDescriptor(code)] as const)).then(
      (entries) => {
        if (!cancelled) setLoaded(Object.fromEntries(entries))
      },
    )
    return () => {
      cancelled = true
    }
  }, [allowedKey])

  const descriptor = (code: FinanceReport) => loaded?.[code] ?? null
  return {
    ready: loaded !== null,
    has: (code) => descriptor(code) !== null,
    descriptor,
    hasDimension: (code, dimension) => descriptor(code)?.dimensions.some((item) => item.key === dimension) ?? false,
  }
}

/**
 * Разделы, которые человек вообще может увидеть. Считаются на сервере
 * (src/auth/sections.ts) с учётом выданных отчётов, скрытых показателей
 * и разделов, скрытых администратором; здесь — только чтение готового.
 */
export function financeAccess(user: CurrentUser) {
  const on = (code: string) => (user.sections ?? []).includes(`finance.${code}`)
  return {
    any: ['overview', 'pnl', 'cash', 'debts', 'margin'].some(on),
    overview: on('overview'),
    pnl: on('pnl'),
    cash: on('cash'),
    debts: on('debts'),
    margin: on('margin'),
  }
}

// --- Строки ------------------------------------------------------------------

/** Значение фильтра для строки: GUID у справочных измерений, текст у текстовых. */
export function filterValue(row: Record<string, unknown>, dimension: string): string {
  return String(row[`${dimension}_guid`] ?? row[dimension] ?? '')
}

export function rowKey(row: Record<string, unknown>, dimension: string): string {
  return filterValue(row, dimension)
}

export function num(value: unknown): number {
  return toNumber(value) ?? 0
}

/** Строки текущего периода с найденной парой из прошлого — по GUID, не по позиции. */
export function indexBy(rows: Record<string, unknown>[] | undefined, dimension: string) {
  const map = new Map<string, Record<string, unknown>>()
  for (const row of rows ?? []) map.set(rowKey(row, dimension), row)
  return map
}

/** Свежесть: самая старая отметка из ответов — по ней видно худший случай. */
export function oldestAsOf(states: Array<ReportState | null | undefined>): string | null {
  const values = states.map((state) => state?.data?.meta.data_as_of).filter((value): value is string => Boolean(value))
  if (values.length === 0) return null
  return values.reduce((min, value) => (value < min ? value : min))
}

/** Хранит последнее непустое значение: пока идёт новый запрос, график не пропадает. */
export function useLatest<T>(value: T | null): T | null {
  const ref = useRef<T | null>(value)
  if (value !== null) ref.current = value
  return ref.current
}
